import type { CohortFilters } from "@/types/cohort";

import {
  enumOptions,
  hiddenText,
  locationIds,
  standardDateRange,
} from "./field-factories";
import { serializeAnalyticsQuery } from "./request-family";
import type { DateRangeFields, LocationIdsField } from "./shared-types";
import {
  defineFilters,
  enumSetFilter,
  setFilter,
} from "@/features/table-filters/contract";
import type {
  FilterDefinition,
  FilterKey,
  FilterValueKind,
} from "@/features/table-filters/contract";

type Mutable<Request extends object> = Record<string, unknown> &
  Partial<Request>;

export type BlastAnalyticsFilters = DateRangeFields & {
  blastNames?: string[];
  blastMediums?: string[];
  blastTypes?: string[];
};

export const BLAST_ANALYTICS_DEFAULT_FILTERS: BlastAnalyticsFilters = {};

export const BLAST_ANALYTICS_FILTER_DEFINITIONS =
  defineFilters<BlastAnalyticsFilters>()([
    standardDateRange<BlastAnalyticsFilters>({
      label: "Date range",
      startQueryKey: "from",
      endQueryKey: "to",
    }),
    setFilter({
      key: "blastNames",
      label: "Blast name",
      queryKey: "blastNames",
    }),
    enumSetFilter({
      key: "blastMediums",
      label: "Medium",
      queryKey: "mediums",
      options: enumOptions(["Marketing", "Transactional"] as const),
    }),
    enumSetFilter({
      key: "blastTypes",
      label: "Type",
      queryKey: "types",
      options: enumOptions(["Email", "SMS"] as const),
    }),
  ]);

export type CallAnalyticsFilters = DateRangeFields & LocationIdsField;

export const CALL_ANALYTICS_DEFAULT_FILTERS: CallAnalyticsFilters = {};

interface CallAnalyticsQueryContext {
  tz: string;
}

const CALL_ANALYTICS_QUERY_CONTEXT_DEFINITIONS =
  defineFilters<CallAnalyticsQueryContext>()([
    hiddenText<CallAnalyticsQueryContext, "tz">("tz", "Timezone"),
  ]);

export const CALL_ANALYTICS_FILTER_DEFINITIONS =
  defineFilters<CallAnalyticsFilters>()([
    standardDateRange<CallAnalyticsFilters>({
      label: "Date range",
      startQueryKey: "from",
      endQueryKey: "to",
    }),
    locationIds<CallAnalyticsFilters>([], {
      control: "location",
      queryKey: "locationIds",
    }),
  ]);

export function serializeBlastAnalyticsFilters(
  filters: Partial<BlastAnalyticsFilters>,
  timezone: string,
): URLSearchParams {
  return serializeAnalyticsQuery([
    { definitions: BLAST_ANALYTICS_FILTER_DEFINITIONS, values: filters },
    {
      definitions: CALL_ANALYTICS_QUERY_CONTEXT_DEFINITIONS,
      values: { tz: timezone },
    },
  ]);
}

export function serializeCallAnalyticsFilters(
  filters: Partial<CallAnalyticsFilters>,
  timezone: string,
): URLSearchParams {
  return serializeAnalyticsQuery([
    { definitions: CALL_ANALYTICS_FILTER_DEFINITIONS, values: filters },
    {
      definitions: CALL_ANALYTICS_QUERY_CONTEXT_DEFINITIONS,
      values: { tz: timezone },
    },
  ]);
}

export type PatientCohortFilterState = Partial<CohortFilters> &
  Record<string, unknown>;

export const PATIENT_COHORT_FILTER_DEFAULTS: PatientCohortFilterState = {};

type PatientFilterUrlKind = "scalar" | "array" | "json" | "dateRange";

const PATIENT_COHORT_FILTER_KEYS = [
  ["locationIds", "array"],
  ["tagIds", "json"],
  ["leadSource", "json"],
  ["age", "array"],
  ["name", "scalar"],
  ["email", "scalar"],
  ["phoneNumber", "scalar"],
  ["createdDate", "dateRange"],
  ["dateOfBirth", "array"],
  ["birthday", "json"],
  ["smsOptOut", "array"],
  ["onSchedulingBlacklist", "array"],
  ["appointmentCount", "array"],
  ["lastAppointmentSince", "json"],
  ["serviceVisitCount", "json"],
  ["visitSpanDays", "array"],
  ["totalSpent", "array"],
  ["membershipIds", "json"],
  ["packageIds", "json"],
  ["appointmentTypeIds", "json"],
  ["appointmentBetween", "json"],
  ["completedAppointmentBetween", "json"],
  ["createdAppointmentBetween", "json"],
  ["noAppointmentBetween", "json"],
  ["hasFutureAppointments", "array"],
  ["cancelledAppointmentDateRange", "dateRange"],
  ["cancelledAppointmentDays", "array"],
  ["lastAppointmentStatus", "array"],
  ["weight", "array"],
  ["gender", "json"],
  ["patientSource", "json"],
  ["preferredProviderId", "json"],
  ["mostRecentProviderId", "json"],
  ["creditBalance", "array"],
  ["outstandingBalance", "array"],
  ["outstandingBalanceDays", "array"],
  ["purchasedItemIds", "json"],
  ["hasPaymentPlan", "array"],
  ["lastPaymentDays", "array"],
  ["lastPaymentStatus", "json"],
  ["hasPaymentMethod", "array"],
  ["membershipStatuses", "json"],
  ["city", "scalar"],
  ["state", "scalar"],
  ["zipCode", "scalar"],
  ["lastActiveDays", "array"],
  ["optedOutOfEmail", "array"],
  ["optedInToSmsMarketing", "array"],
  ["optedInToEmailMarketing", "array"],
  ["optedInToSocialMedia", "array"],
  ["height", "array"],
  ["goalWeight", "array"],
  ["systolicBp", "array"],
  ["diastolicBp", "array"],
  ["waistCircumference", "array"],
  ["pulse", "array"],
  ["temperature", "array"],
  ["hasProfilePicture", "array"],
  ["pronouns", "scalar"],
  ["preferredName", "scalar"],
  ["surchargeDisabled", "array"],
  ["hasNotes", "array"],
  ["hasActiveChat", "array"],
  ["medicationCount", "array"],
  ["hasEmail", "array"],
  ["hasPhoneNumber", "array"],
  ["hasMembership", "array"],
  ["hasTags", "array"],
] as const satisfies readonly (readonly [string, PatientFilterUrlKind])[];

/** URL keys a filter used to publish under, kept parseable so a dashboard link
 *  shared before the filter changed shape still hydrates instead of quietly
 *  dropping its constraint and showing a wider patient set.
 *
 *  `appointmentBetween` was `appointmentDateRange`, a bare date range with no
 *  service or status side, so an old link lifts straight into the compound
 *  value's `dateRange` slot. The sibling compound filters need no entry — they
 *  were introduced already compound and never had a flat key in a URL. */
export const LEGACY_PATIENT_COHORT_URL_KEYS: Record<
  string,
  {
    key: string;
    kind: PatientFilterUrlKind;
    lift: (value: unknown) => unknown;
  }
> = {
  appointmentBetween: {
    key: "appointmentDateRange",
    kind: "array",
    lift: (value) => ({ dateRange: value }),
  },
};

function hasMeaningfulValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) {
    return value.some(
      (item) => item !== undefined && item !== null && item !== "",
    );
  }
  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  return true;
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function parseArrayValues(values: string[]): unknown[] | undefined {
  if (values.length === 0) return undefined;
  const parsed = values.map((value) => {
    if (value === "") return null;
    const maybeNumber = Number(value);
    return Number.isFinite(maybeNumber) && String(maybeNumber) === value
      ? maybeNumber
      : value;
  });
  return hasMeaningfulValue(parsed) ? parsed : undefined;
}

function readUrlValue(
  params: URLSearchParams,
  key: string,
  kind: PatientFilterUrlKind,
): unknown {
  const values = params.getAll(key);
  if (values.length === 0) return undefined;
  // Relative ranges are objects; explicit bounds keep their repeated-key URL
  // shape so previously shared date-range links still hydrate.
  if (kind === "dateRange") {
    const parsed = parseJson(values[values.length - 1]);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
    return values.length === 2 ? parseArrayValues(values) : undefined;
  }
  return kind === "json"
    ? parseJson(values[values.length - 1])
    : kind === "array"
      ? parseArrayValues(values)
      : values[values.length - 1];
}

function createPatientCohortDefinition(
  key: string,
  kind: PatientFilterUrlKind,
): FilterDefinition<PatientCohortFilterState> {
  const filterKey = key as FilterKey<PatientCohortFilterState>;
  const valueKind: FilterValueKind = kind === "scalar" ? "string" : "set";
  const legacy = LEGACY_PATIENT_COHORT_URL_KEYS[key];

  return {
    key: filterKey,
    requestKeys: [filterKey],
    urlKeys: legacy ? [key, legacy.key] : [key],
    label: key,
    value: valueKind,
    operator: kind === "scalar" ? "contains" : "anyOf",
    control: "none",
    contract: {
      normalize(value: unknown) {
        return hasMeaningfulValue(value) ? value : undefined;
      },
    },
    urlPolicy: "scalar",
    presented: false,
    query: false,
    normalizeRequest(request) {
      const next = { ...request } as Mutable<PatientCohortFilterState>;
      if (!hasMeaningfulValue(next[key])) delete next[key];
      return next;
    },
    writeQuery() {},
    parseUrl(params) {
      const value = readUrlValue(params, key, kind);
      if (hasMeaningfulValue(value)) {
        return { [key]: value } as Partial<PatientCohortFilterState>;
      }
      // Only reached when the current key is absent, so a link carrying both
      // keys still takes the current one.
      if (legacy) {
        const legacyValue = readUrlValue(params, legacy.key, legacy.kind);
        if (hasMeaningfulValue(legacyValue)) {
          return {
            [key]: legacy.lift(legacyValue),
          } as Partial<PatientCohortFilterState>;
        }
      }
      return {};
    },
    writeUrl(params, request) {
      params.delete(key);
      // Drop the superseded key too, so hydrating an old link and then changing
      // the filter cannot leave the stale range behind to be read back later.
      if (legacy) params.delete(legacy.key);
      const value = (request as Mutable<PatientCohortFilterState>)[key];
      if (!hasMeaningfulValue(value)) return;
      if (kind === "json" || (kind === "dateRange" && !Array.isArray(value))) {
        params.append(key, JSON.stringify(value));
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) =>
          params.append(key, item == null ? "" : String(item)),
        );
        return;
      }
      params.append(key, String(value));
    },
    readControl(request) {
      return (request as Mutable<PatientCohortFilterState>)[key];
    },
    writeControl(request, value) {
      return { ...request, [key]: value };
    },
  };
}

export const PATIENT_COHORT_FILTER_DEFINITIONS =
  defineFilters<PatientCohortFilterState>()(
    PATIENT_COHORT_FILTER_KEYS.map(([key, kind]) =>
      createPatientCohortDefinition(key, kind),
    ),
  );

export const PATIENT_COHORT_FILTER_KEYS_FOR_TESTS = PATIENT_COHORT_FILTER_KEYS;
